import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://headspa-booking.preview.emergentagent.com';

const BookingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    service_id: '',
    booking_date: '',
    booking_time: '',
    full_name: '',
    email: '',
    phone: '',
    birth_date: '',
    document_id: '',
    payment_method: 'transfer',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchServices();
    }
  }, [isOpen]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Error al cargar servicios');
    }
  };

  const fetchAvailability = async () => {
    if (!formData.booking_date || !formData.service_id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/availability`, {
        params: {
          date: formData.booking_date,
          service_id: formData.service_id
        }
      });
      setAvailableTimes(response.data.available_times || []);
    } catch (error) {
      toast.error('Error al verificar disponibilidad');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.booking_date && formData.service_id) {
      fetchAvailability();
    }
  }, [formData.booking_date, formData.service_id]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/public/booking-request`, formData);
      toast.success(response.data.message, { duration: 5000 });
      onClose();
      setFormData({
        service_id: '',
        booking_date: '',
        booking_time: '',
        full_name: '',
        email: '',
        phone: '',
        birth_date: '',
        document_id: '',
        payment_method: 'transfer',
        notes: ''
      });
      setStep(1);
    } catch (error) {
      const errorMsg = error.response?.data?.detail?.error || 'Error al crear reserva';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && !formData.service_id) {
      toast.error('Por favor selecciona un servicio');
      return;
    }
    if (step === 2 && (!formData.booking_date || !formData.booking_time)) {
      toast.error('Por favor selecciona fecha y horario');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2 }
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <motion.div
        className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden mx-4"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <div className="relative bg-[#C9A86A] p-4 sm:p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <h2 className="text-2xl sm:text-3xl font-light">Reservar Experiencia</h2>
          <p className="text-white/80 mt-2 text-sm sm:text-base">Paso {step} de 3</p>

          <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-white"
              initial={{ width: '33%' }}
              animate={{ width: `${(step / 3) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {step === 1 && (
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-xl sm:text-2xl font-medium text-gray-800 mb-4">Selecciona tu Servicio</h3>
              <div className="grid gap-3 sm:gap-4">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setFormData({ ...formData, service_id: service.id })}
                    className={`relative p-4 sm:p-6 rounded-2xl border-2 text-left transition-all ${
                      formData.service_id === service.id
                        ? 'border-[#C9A86A] bg-gradient-to-r from-[#C9A86A]/10 to-[#6B7F5C]/10'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1">
                        <h4 className="text-lg sm:text-xl font-medium text-gray-800">{service.name}</h4>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">{service.description || ''}</p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-2">{service.duration_minutes} minutos</p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-xl sm:text-2xl font-bold text-[#C9A86A]">${service.price.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{service.currency}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-medium text-gray-800 mb-4">Fecha y Horario</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha</label>
                <input
                  type="date"
                  value={formData.booking_date}
                  onChange={(e) => setFormData({ ...formData, booking_date: e.target.value, booking_time: '' })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#C9A86A] focus:outline-none transition-colors"
                />
              </div>

              {availableTimes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Horarios Disponibles</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {availableTimes.map((time) => (
                      <button
                        key={time}
                        onClick={() => setFormData({ ...formData, booking_time: time })}
                        className={`py-3 rounded-xl border-2 font-medium transition-all ${
                          formData.booking_time === time
                            ? 'border-[#C9A86A] bg-[#C9A86A] text-white'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {loading && <p className="text-center text-gray-500">Cargando horarios...</p>}
              {formData.booking_date && availableTimes.length === 0 && !loading && (
                <p className="text-center text-red-500">No hay horarios disponibles para esta fecha</p>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-medium text-gray-800 mb-4">Tus Datos</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre Completo *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#C9A86A] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#C9A86A] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#C9A86A] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Nacimiento *</label>
                  <input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#C9A86A] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Documento *</label>
                  <input
                    type="text"
                    value={formData.document_id}
                    onChange={(e) => setFormData({ ...formData, document_id: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#C9A86A] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago *</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#C9A86A] focus:outline-none"
                  >
                    <option value="transfer">Transferencia</option>
                    <option value="mercadopago">Mercado Pago</option>
                    <option value="cash">Efectivo</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notas (opcional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#C9A86A] focus:outline-none resize-none"
                  placeholder="Algún comentario o solicitud especial..."
                />
              </div>

              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Importante:</strong> Tu reserva quedará confirmada una vez que recibamos la seña.
                  Te contactaremos para coordinar el pago.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-200 flex justify-between gap-3">
          <button
            onClick={prevStep}
            disabled={step === 1}
            className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base text-gray-600 font-medium rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Atrás
          </button>

          {step < 3 ? (
            <button
              onClick={nextStep}
              className="px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base bg-[#C9A86A] text-white font-medium rounded-full shadow-lg hover:scale-105 transition-transform"
            >
              Continuar
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base bg-[#C9A86A] text-white font-medium rounded-full shadow-lg disabled:opacity-50 hover:scale-105 transition-transform"
            >
              {loading ? 'Enviando...' : 'Confirmar Reserva'}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BookingModal;
